import {useMemo, useRef} from "react";
import {css} from '@emotion/react';
import {useLocation, useNavigate} from "react-router-dom";
import {slideLtrClassNames, Text} from "@netapp/bxp-design-system-react";
import {useSelector} from "react-redux";
import {RootState} from "store";
import {compact, uniqBy, orderBy} from "lodash";
import { CSSTransition } from "react-transition-group";
import navDataTest from "testUtils/navDataTest.ts";

const mainNavCss = css`
    display: grid;
    grid-template-columns: 1fr auto;
    height: calc(100vh - 72px);
    position: fixed;
    top: 72px;
`



const navCss = css`
    display: flex;
    flex-direction: column;
    background-color: var(--shell-main-nav-bg-v2);
    overflow-y: auto;
    padding-top: 8px;
    width: 352px;
    z-index: 2;
    border-right: 1px solid var(--border-main);

`
const maskCss = css`
    background-color: var(--mask-background);
    opacity: 0.5;
    width: 100vw;
    height: 100vh;
    position: fixed;
    top: 72px;
`

const itemCss = (isActive) => css`
    display: flex;
    padding: 8px 40px 16px 40px;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 4px;
    &:hover {
        background-color: var(--shell-main-nav-bg-hover-v2);
        cursor: pointer;
    }
    background-color: ${isActive ? "var(--background-hover)" : "none"};
    border-left: 8px solid ${isActive ? "var(--shell-main-nav-icon-text-selected-v2)" : "transparent"};
`

const Item = ({label, description, to}:{label: string, description: string, to: string}) => {
    const navigate = useNavigate()
    const location = useLocation();
    const locationRootPath = location.pathname.split("/")[1] || location.pathname.split("/")[0]
    const toRootPath = to.split("/")[1] || to.split("/")[0]
    const isActive = locationRootPath === toRootPath

    return <div css={itemCss(isActive)} onClick={()=> {navigate({pathname: to})}}>
        <Text style={isActive ? {color: "var(--icon-primary)"} : {}} bold level={14}>{label}</Text>
        <Text color={"text-secondary"} level={13}>{description}</Text>
    </div>
}

const navPriorityList = ["overview", "fsx", "builders", "ai", "databases", "vmware", "administration"];

const Nav = ({setCloseMenu, isNavOpen}: {setCloseMenu: React.Dispatch<React.SetStateAction<boolean>>, isNavOpen:boolean}) => {
    const nodeRef = useRef<HTMLDivElement>(null);
    const workloadConfigs = useSelector((state: RootState) => state.services.workloadConfigs);
    const activeFeatures = useSelector((state: RootState) => state.features.active)
    const filteredWorkloadConfigs = useMemo(() => orderBy(workloadConfigs.filter(config => !!config.serviceNav && !config.serviceNav.hideFromMenu && (!config.serviceNav.featureId || activeFeatures[config.serviceNav.featureId])), ["name"], ["desc", "asc"]),
        [activeFeatures, workloadConfigs]);
    const priorityWorkloadConfigs = useMemo(() => {
        return compact(navPriorityList.map(priorityItem => {
            return filteredWorkloadConfigs.find(config => config.id.toLowerCase() === priorityItem.toLowerCase())
        }))
    },[filteredWorkloadConfigs]);
    const navItems = useMemo(() => {
        return uniqBy(priorityWorkloadConfigs.concat(filteredWorkloadConfigs), "id")
    },[priorityWorkloadConfigs, filteredWorkloadConfigs]);

    return <CSSTransition timeout={500} exit={!isNavOpen} in={isNavOpen} nodeRef={nodeRef} unmountOnExit classNames={slideLtrClassNames}>
        <div ref={nodeRef} css={navCss}>
            {navItems.map(item => {
                return <div data-testid={`${navDataTest.navItem}${item.serviceNav.label}`} key={item.id} onClick={()=> setCloseMenu(false)}>
                    <Item label={item.serviceNav.label} to={item.to} description={item.serviceNav?.description} />
                </div>
            })}
        </div>
    </CSSTransition>
}

const Mask = ({setIsNavOpen}: {setIsNavOpen: React.Dispatch<React.SetStateAction<boolean>>}) => {
    return <div onClick={()=> {setIsNavOpen(false)}} css={maskCss}/>
}

const MainNav = ({setIsNavOpen, isNavOpen}: {setIsNavOpen: React.Dispatch<React.SetStateAction<boolean>>, isNavOpen: boolean}) => {
    return <div css={mainNavCss}>
                <Nav setCloseMenu={setIsNavOpen} isNavOpen={isNavOpen}/>
                {isNavOpen && <Mask setIsNavOpen={setIsNavOpen}/>}
            </div>
}

export default MainNav;